import React, { useCallback, useState } from 'react';

import { DOC_STATE } from '../../constants/patientCard';
import { getAdmissionDocumentContent } from '../../services/api/admissionDischargeApi';
import {
  DOC_BUTTON_META, NOT_ISSUED_DIAGNOSTICS_TEXT, CANCELLED_NOTE, bandedLabel, reasonFor,
} from '../../utils/admissionDocumentLabels';

/**
 * AdmissionDocumentButtons — F8. Renders the three document buttons
 * (Discharge Summary / Diagnostics Bill / Invoice) for one admission, driven
 * ONLY by the §1.4 `DocumentDescriptor` list already resolved into memory
 * (`IpdTimelineRow` reads it off `sections.documents`).
 *
 * THE RULE THAT MATTERS: this module never calls a PDF endpoint to find out
 * whether a document exists — `available` / `state` / `unavailable_reason_code`
 * come straight off the descriptor. A click opens the §1.5 PURE content route
 * (`getAdmissionDocumentContent`) — never a legacy minting print route, never
 * a raw browser-tab open, never prefetched, never on mount or expand.
 *
 * `DOC_BUTTON_META` / `bandedLabel` / `reasonFor` moved to
 * `utils/admissionDocumentLabels.js` (F10) — the SAME pure derivation is
 * reused by `printPatientCard.js` for the static (non-clickable) document
 * lines on the printed card, so the two surfaces can never disagree on
 * wording. That module has no API import, unlike this one.
 */

// A click can still 409/404 even on an `available` button (a rare race — the
// row read is a snapshot). Renders inline, never a toast. Distinct from
// `REASON_TEXT.NOT_ISSUED` above: that fires off the descriptor BEFORE any
// click (nothing to do); this fires when the click reveals the row's read
// was stale — the useful next step is "refresh", not "there's nothing here".
const OPEN_ERROR_TEXT = {
  DOCUMENT_NOT_ISSUED: "This document hasn't been issued yet. Refresh the page to see its current status.",
  NO_LAB_CHARGES: NOT_ISSUED_DIAGNOSTICS_TEXT,
  ADMISSION_NOT_FOUND: "We couldn't load this admission. Refresh the page and try again.",
};
const OPEN_ERROR_FALLBACK = "This document couldn't be opened. Try again in a moment.";

/** `documents` — the array of `DocumentDescriptor` for THIS admission only (already matched by the caller). */
export default function AdmissionDocumentButtons({ admissionId, documents }) {
  const [openError, setOpenError] = useState(null); // { docType, message }
  const [loadingDocType, setLoadingDocType] = useState(null);

  const handleOpen = useCallback(async (docType, label) => {
    setOpenError(null);
    setLoadingDocType(docType);
    const result = await getAdmissionDocumentContent(admissionId, docType, label);
    setLoadingDocType(null);
    if (!result.success) {
      setOpenError({ docType, message: OPEN_ERROR_TEXT[result.code] || OPEN_ERROR_FALLBACK });
    }
  }, [admissionId]);

  if (!Array.isArray(documents) || documents.length === 0) return null;

  return (
    <div className="flex flex-wrap items-start gap-2" data-testid={`admission-doc-buttons-${admissionId}`}>
      {DOC_BUTTON_META.map((meta) => {
        const descriptor = documents.find((d) => d.doc_type === meta.docType);
        if (!descriptor) return null;

        const disabled = !descriptor.available;
        const label = bandedLabel(meta, descriptor.state);
        const reason = disabled ? reasonFor(meta.docType, descriptor.unavailable_reason_code) : null;
        const cancelledNote = !disabled && descriptor.state === DOC_STATE.CANCELLED ? CANCELLED_NOTE : null;
        const busy = loadingDocType === meta.docType;
        const errorForThis = openError?.docType === meta.docType ? openError.message : null;

        return (
          <div key={meta.docType} className="flex flex-col">
            <button
              type="button"
              disabled={disabled || busy}
              onClick={() => handleOpen(meta.docType, label)}
              data-testid={`doc-button-${admissionId}-${meta.docType}`}
              title={reason || undefined}
              className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap disabled:cursor-not-allowed ${
                disabled
                  ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500'
                  : descriptor.cancelled
                    ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/50'
                    : 'bg-white dark:bg-slate-800 border-slate-400 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              <span aria-hidden="true">{meta.icon}</span>
              {busy ? 'Opening…' : label}
            </button>
            {reason && (
              <span className="mt-1 max-w-[11rem] text-[10.5px] text-slate-500 dark:text-slate-400">{reason}</span>
            )}
            {cancelledNote && (
              <span className="mt-1 max-w-[11rem] text-[10.5px] text-slate-500 dark:text-slate-400">{cancelledNote}</span>
            )}
            {errorForThis && (
              <span
                data-testid={`doc-button-error-${admissionId}-${meta.docType}`}
                className="mt-1 max-w-[11rem] text-[10.5px] text-rose-600 dark:text-rose-400"
              >
                {errorForThis}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
